import React, { useEffect, useState, useCallback } from "react";
import "./App.css";
import { SITE_CONFIG } from "./lib/config";
import { T } from "./lib/i18n";
import { captureUTM, trackEvent } from "./lib/analytics";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Sections from "./components/Sections";
import Quiz from "./components/Quiz";
import FAQ from "./components/FAQ";
import FinalCTA from "./components/FinalCTA";
import StickyAndFloat from "./components/StickyAndFloat";
import Footer from "./components/Footer";

const LANG_KEY = "havo_lang";

function App() {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem(LANG_KEY) || "uz";
    } catch (_) {
      return "uz";
    }
  });

  const onChangeLang = useCallback((next) => {
    setLang(next);
    try {
      localStorage.setItem(LANG_KEY, next);
    } catch (_) {}
    document.documentElement.lang = next === "uz" ? "uz" : "ru";
    trackEvent("LanguageSwitch", { language: next });
  }, []);

  // SEO title/desc + UTM capture + scroll depth
  useEffect(() => {
    document.title = SITE_CONFIG.seo.title[lang];
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", SITE_CONFIG.seo.description[lang]);
    document.documentElement.lang = lang === "uz" ? "uz" : "ru";
  }, [lang]);

  useEffect(() => {
    captureUTM();
    trackEvent("PageView", { language: lang });
    const depths = [50, 75, 90];
    const fired = new Set();
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      if (total <= 0) return;
      const p = Math.round((h.scrollTop / total) * 100);
      depths.forEach((d) => {
        if (p >= d && !fired.has(d)) {
          fired.add(d);
          trackEvent(`ScrollDepth${d}`, { language: lang });
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reveal-on-scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [lang]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const ctx = { lang, T, scrollTo };

  return (
    <div className="min-h-screen bg-ink-900 text-white">
      <Header {...ctx} onChangeLang={onChangeLang} />
      <main>
        <Hero {...ctx} />
        <Sections {...ctx} />
        <Quiz {...ctx} />
        <FAQ {...ctx} />
        <FinalCTA {...ctx} />
      </main>
      <Footer {...ctx} />
      <StickyAndFloat {...ctx} />
    </div>
  );
}

export default App;
